// React Query mutation hooks for Health Encyclopedia.
// Slice 4 adds category create / rename / delete. Content create / update /
// delete (Slices 5 / 7) extend this file.
//
// Category mutations invalidate the whole `health-encyclopedia` query tree:
// a rename changes the category name shown on list rows, and add/delete shift
// the per-category counts in the filter dropdown — so both the list and the
// categories query must refresh.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { healthEncyclopediaApi } from '../api/health-encyclopedia.adapter';
import type {
  Category,
  CategoryNameInput,
  ContentDetail,
  CreateContentInput,
  UpdateContentInput,
  UploadResult,
} from '../types/health-encyclopedia.types';
import { healthEncyclopediaQueryKeys } from './use-health-encyclopedia';

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation<Category, Error, CategoryNameInput>({
    mutationFn: (input) => healthEncyclopediaApi.createCategory(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: healthEncyclopediaQueryKeys.all });
    },
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation<Category, Error, { id: number; input: CategoryNameInput }>({
    mutationFn: ({ id, input }) => healthEncyclopediaApi.updateCategory(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: healthEncyclopediaQueryKeys.all });
    },
  });
}

// PRD §3.4 — BE rejects deleting a category that still has content
// (CATEGORY_IN_USE); the UI also disables the ✕ in that case. Root only.
export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => healthEncyclopediaApi.deleteCategory(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: healthEncyclopediaQueryKeys.all });
    },
  });
}

// PRD §3.5 — Save Draft (DRAFT) / Publish (PUBLISHED) for new content. BE
// assigns the EID. Invalidating the whole tree refreshes the list + the
// category counts (the new content bumps its category's count); the detail
// cache is seeded so an immediate re-open skips the spinner.
export function useCreateContent() {
  const qc = useQueryClient();
  return useMutation<ContentDetail, Error, CreateContentInput>({
    mutationFn: (input) => healthEncyclopediaApi.createContent(input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: healthEncyclopediaQueryKeys.all });
      qc.setQueryData(healthEncyclopediaQueryKeys.detail(created.eid), created);
    },
  });
}

// PRD §3.6 — edits existing content. BE enforces the PUBLISHED→DRAFT block
// (the UI also hides Save Draft for currently-PUBLISHED rows).
export function useUpdateContent() {
  const qc = useQueryClient();
  return useMutation<ContentDetail, Error, { eid: string; input: UpdateContentInput }>({
    mutationFn: ({ eid, input }) => healthEncyclopediaApi.updateContent(eid, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: healthEncyclopediaQueryKeys.all });
      qc.setQueryData(healthEncyclopediaQueryKeys.detail(updated.eid), updated);
    },
  });
}

// PRD §3.8 — delete content (Root only; BE enforces). Invalidating the tree
// refreshes the list + the category counts (the removed content drops its
// category's count).
export function useDeleteContent() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (eid) => healthEncyclopediaApi.deleteContent(eid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: healthEncyclopediaQueryKeys.all });
    },
  });
}

// PRD §3.9 — patient-home exposure toggle (SHCT-486). Invalidating the whole tree
// is required rather than a targeted row patch: one toggle changes both the row's
// own state AND `homeExposedCount`, which in turn decides whether every OTHER
// unchecked toggle in the table is disabled.
export function useSetHomeExpose() {
  const qc = useQueryClient();
  return useMutation<ContentDetail, Error, { eid: string; homeExposed: boolean }>({
    mutationFn: ({ eid, homeExposed }) => healthEncyclopediaApi.setHomeExpose(eid, homeExposed),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: healthEncyclopediaQueryKeys.all });
      qc.setQueryData(healthEncyclopediaQueryKeys.detail(updated.eid), updated);
    },
  });
}

// PRD §3.5 / §3.7 — thumbnail + image-block upload (feature-specific endpoint).
// Returns the stored URL (mock: a base64 data: URL). No cache to invalidate.
export function useUploadImage() {
  return useMutation<UploadResult, Error, File>({
    mutationFn: (file) => healthEncyclopediaApi.uploadImage(file),
  });
}
